import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig
} from 'axios';
import Configuration from '../../config/Configuration';
import { IUserMetadata } from '../../authentication/interfaces/AuthInterface';
import AuthService from '../../authentication/services/AuthService';
import _ from 'lodash';
import React from 'react';
import {
  Dispatcher,
  DISPATCHER_ALL_UPLOAD_CANCELED,
  DISPATCHER_UPLOAD_CANCELED,
  WarningType
} from '../../../index';
import LocalCache from '../../../utility/LocalCache';
// @ts-ignore
import Properties from 'properties';
import { EXTENDED_AXIOS_RESPONSE } from './Types';

declare module 'axios' {
  interface AxiosRequestConfig {
    urlParams?: Record<string, string>;
  }
}

export const ERROR_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  UNPROCESSABLE_ENTITY: 422,
  PRECONDITION_REQUIRED: 428,
  TOO_MANY_REQUESTS: 429,
  REQUEST_TIMEOUT: 408,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_FILE: 415,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  NETWORK_AUTHENTICATION_REQUIRED: 511,
  LOGIN_TIME_OUT: 440
};

export const AxiosInterceptor: Function = () => {
  let appXCorrelationID: any;
  const intercept = (axiosInstance: AxiosInstance) => {
    axiosInstance = axiosInstance || axios; // if there is no axios instance use default axios

    /**
     * Sets authorization header
     * @param {AxiosRequestConfig} config
     * @param {IUserMetadata} user
     */
    let setAuthorizationHeader = (config: AxiosRequestConfig, user?: IUserMetadata) => {
      let isFormData = _.get(config, 'data.get'); // note, this line only checks for form data
      let isAuthRest = isFormData && config.data.get('isAuthRest'); // here we need to use full path otherwise using
      // isFormData will cause browser security breach and will be canceled
      const impersonatedUser = LocalCache.getItem('impersonated', 'sessionStorage'); // if user is impersonated token should be omitted from header
      if (!isAuthRest && user && !impersonatedUser) {
        axiosInstance.defaults.headers.common['Authorization'] = `${user.token_type} ${user.access_token}`;
        let bearer = user.token_type.charAt(0).toUpperCase() + user.token_type.slice(1);
        config.headers['Authorization'] = `${bearer} ${user.access_token}`;
      } else {
        if (!impersonatedUser) {
          const { authorization } = Configuration.get().AUTH;
          config.headers['Authorization'] = authorization;
        }
        if (_.get(config, 'data.params')) {
          delete config.data.params.isAuthRest;
          if (_.isEmpty(config.data.params)) {
            delete config.data.params;
          }
        }

        if (isFormData) {
          // removes auth rest flag
          config.data.delete('isAuthRest');
        }
      }

      // set xCorrelationID header
      appXCorrelationID && (config.headers['X-Correlation-ID'] = appXCorrelationID);

      return config;
    };

    /**
     * Create axios interceptor. Should update any matched request url and redirect to a mocked json file
     */
    axiosInstance.interceptors.request.use(async (config) => {
      // Update request before sending
      let urlParams = _.get(config, 'data.urlParams') || _.get(config, 'params.urlParams');
      if (config.url && _.isObject(urlParams)) { // if there are url params then add them as path variables
        const currentUrl = new URL(config.url, config.baseURL);
        // parse pathName to implement variables
        Object.entries(urlParams || {}).forEach(([
          param,
          value
        ]) => {
          currentUrl.pathname = currentUrl.pathname.replace(`:${param}`, encodeURIComponent(value));
        });
        const { NAME: name } = Configuration.get().REST_SERVER;
        const urlPrefix = process.env.NODE_ENV === 'development' && name ? `/${name}` : '';

        config = {
          ...config,
          baseURL: `${currentUrl.protocol}//${currentUrl.host}`,
          url: `${urlPrefix}${currentUrl.pathname}`
        };

        _.get(config, 'params.urlParams') && delete config.params.urlParams;
        _.get(config, 'data.urlParams') && delete config.data.urlParams;
      }

      if (AuthService.isRunning()) {
        return AuthService.getUser().then((user: IUserMetadata | null | any) => {
          if (user
            // <debug>
            || AuthService.useStoryHelper
            // </debug>
          ) {
            config = setAuthorizationHeader(config, user as any);
            return config;
          } else {
            let isAuthRest = config.data && _.isFunction(config.data.get) && config.data.get('isAuthRest'); // here we need to use full path otherwise using
            if (isAuthRest) {
              return setAuthorizationHeader(config);
            }
            AuthService.onUserSignedOut().catch(e => e);
          }
          return config;
        }).catch(e => e);
      }
      return config;
    });

    /**
     * Handle all errors
     */
    axiosInstance.interceptors.response.use(
      (response: EXTENDED_AXIOS_RESPONSE): Promise<any> => {
        if (response) {
          // catch xCorrelationID header
          if (response.headers) {
            appXCorrelationID = response.headers['x-correlation-id'];
          }

          // request is successful
          if (response.status === 200) {
            // there is user error message in response
            if (response.data.success === false) {
              let maxUploadSizeExceeded = _.get(response, 'data.maxUploadSizeExceeded');
              if (_.isNumber(maxUploadSizeExceeded)) {
                let files = response.config.data.getAll('file') || [];
                let errors = files.map((item: File) => ({
                  success: false,
                  maxUploadSizeExceeded,
                  showWarning: {
                    code: 'UPLOAD_TOO_LARGE',
                    hasClose: true
                  },
                  fileSize: item.size,
                  fileName: item.name
                }));
                response.errors = errors;
              } else {
                const { reasons, description, errorCode = null, type } = response.data;
                (response as any).showWarning = {
                  errorCode,
                  hasCancel: true,
                  hasEmailLink: !!errorCode,
                  text: description || response.data.desciption,
                  title: reasons,
                  type: _.lowerCase(type)
                };
              }
            }

            // set app version
            let version = response.data.version;
            let current = LocalCache.getItem('version');
            if (version && version !== current) {
              if (current) {
                Dispatcher.dispatch('versionChanged', 'end');
              }

              LocalCache.setItem('version', version);
            }
          }

          /**
           * HEAD method is being used only to test resources, if a resource is available
           * STATUS 200 means the resource is available
           * HEADER resource-total-count is used to get more info on what resources are available e.g. a count of resources
           */
          if (response.status === 200 && response.config.method === 'head') {
            let rtc: any = unescape(response.headers['resource-total-count']);

            response.data = {
              resourceExists: true,
              resourceTotalCount: rtc
            };
          }

          return new Promise(resolve => resolve(response));
        }
        return new Promise((resolve, reject) => reject(new Error('signing out')));
      }, (error: any) => {
        /**
         * Request method is HEAD
         * and a server STATUS is 404
         * means resource is not available
         * THIS IS NOT FAILED REQUEST
         */
        if (ERROR_CODES.NOT_FOUND === error.response?.status && error.response.config.method === 'head') {
          return new Promise((resolve, reject) => resolve({
            status: 200, // fake status to get proper handling here, this is not request 404 but RESOURCE 404, meaning resource is not available
            data: {
              resourceExists: false
            }
          }));
        }

        if (error.code === 'ECONNABORTED') {
          error.showWarning = {
            hasOk: true,
            placeholders: {
              url: error.config.url
            },
            code: 'REQUEST_TIMEOUT'
          };
        } else if (error.response && error.config) {
          let errorMap = {} as any;
          Object.keys(ERROR_CODES).forEach((key: any) => {
            if (ERROR_CODES.hasOwnProperty(key)) {
              let index = ERROR_CODES[key];
              errorMap[index] = key as string;
            }
          });

          error.showWarning = {
            code: errorMap[error.response.status],
            closable: false // override default configuration, removes x from dialogs
          };

          // noinspection FallThroughInSwitchStatementJS
          switch (error.response.status) {
            case ERROR_CODES.REQUEST_TIMEOUT:
              _.assign(error.showWarning, {
                hasOk: true,
                placeholders: {
                  url: error.config.url
                }
              });
              break;
            case ERROR_CODES.UNAUTHORIZED:
              const reasons = _.get(error, 'response.data.reasons');
              if (_.get(error, 'response.data.sessionExpired')) {
                _.assign(error.showWarning, {
                  hasLogoutOk: true,
                  title: reasons,
                  text: _.get(error, 'response.data.description')
                });
              } else if (_.get(error, 'response.data.nonExistingUser')) {
                _.assign(error.showWarning, {
                  hasNonExistingUser: true,
                  title: reasons,
                  text: _.get(error, 'response.data.description')
                });
              } else {
                _.assign(error.showWarning, {
                  // hasOk: true,
                  // hasTryToLogin: true,
                  hasTryToLoginOK: true
                });
              }
              break;
            case ERROR_CODES.LOGIN_TIME_OUT:
              _.assign(error.showWarning, {
                hasOk: true,
                hasLogin: true
              });
              break;
            case ERROR_CODES.TOO_MANY_REQUESTS:
              let rateLimit = error.response.headers['ratelimit-reset'];
              _.assign(error.showWarning, {
                hasClose: true,
                closeAllPreviousWarning: true,
                placeholders: {
                  seconds: rateLimit
                },
                type: WarningType.error
              });
              break;
            case ERROR_CODES.BAD_REQUEST:
              const url = _.get(error, 'config.url');
              let errorTitle = _.get(error, 'response.data.error');
              let errorCode = _.get(error, 'response.data.errorCode');
              let errorDescription = _.get(error, 'response.data.error_description');
              if (!errorDescription && _.get(error, 'response.data.messages')) {
                errorDescription = _.get(error, 'response.data.messages').join('\n');
              }
              if (url && errorTitle !== 'invalid_grant') {
                _.assign(error.showWarning, {
                  hasOk: true,
                  // hasTryToLogin: !url.match('auth/token'), //TODO check if we still need this on 400 error status
                  hasEmailLink: errorCode || (url.match('auth/token') && errorTitle),
                  title: errorTitle,
                  text: errorDescription
                });
                break;
              } else if (errorTitle === 'invalid_grant') {
                _.assign(error, {
                  showWarning: {
                    badCredentials: true
                  }
                });
              }
              break;
            /* falls through */
            case ERROR_CODES.FORBIDDEN:
            case ERROR_CODES.NOT_FOUND:
            case ERROR_CODES.METHOD_NOT_ALLOWED:
            case ERROR_CODES.PAYLOAD_TOO_LARGE:
            case ERROR_CODES.UNSUPPORTED_MEDIA_FILE:
            case ERROR_CODES.INTERNAL_SERVER_ERROR:
            case ERROR_CODES.NOT_IMPLEMENTED:
            case ERROR_CODES.BAD_GATEWAY:
            case ERROR_CODES.PRECONDITION_REQUIRED:
            case ERROR_CODES.NETWORK_AUTHENTICATION_REQUIRED:
              const { response } = error;
              let additionalData = {};
              if (_.get(response, 'data.messages')) {
                additionalData = {
                  text: response.data.messages.join('\n')
                };
              }
              _.assign(error.showWarning, {
                hasOk: true,
                ...additionalData
              });
              break;
            default:
              _.assign(error.showWarning, {
                hasOk: true,
                code: 'DEFAULT_ERROR'
              });
              break;
          }
        } else if ([DISPATCHER_UPLOAD_CANCELED, DISPATCHER_ALL_UPLOAD_CANCELED].indexOf(error.message) !== -1) {
          _.set(error, 'showWarning', {
            hasOk: true,
            code: error.message
          });
        } else if (error.message === 'Network Error') {
          _.set(error, 'showWarning', {
            hasOk: true,
            code: 'NETWORK_ERROR'
          });
        }

        return new Promise((resolve, reject) => reject(error));
      }
    );
  };

  return {
    intercept
  };
};

export default AxiosInterceptor();
